import * as z from 'zod';
import { BookingMode } from '../types/order-main';

const allowedBookingModes = ['houseConnection', 'infrastructure', 'productOnly', 'houseConnectionAndProduct'] as const;

const initBookingModeSchema = z.enum(allowedBookingModes, {
  error: issue => {
    if (!issue.input) {
      return { message: 'Es wurde kein Buchungsmodus übergeben' };
    }
    if (issue.code === 'invalid_value') {
      return { message: 'Der übergebene Buchungsmodus wird nicht unterstützt' };
    }

    return { message: 'Ungültiger Wert' };
  },
});

function tryParseNumber(data: string, ctx: z.RefinementCtx): number | never {
  const parsed = parseInt(data, 10);
  if (Number.isNaN(parsed)) {
    ctx.addIssue({ code: 'custom', message: 'Wert ist keine Ganzzahl' });

    return z.NEVER;
  }

  return parsed;
}

function numberString(params?: Parameters<typeof z.string>[0]) {
  return z.string(params).transform(tryParseNumber);
}

const nullableNumberString = z.string()
  .nullable()
  .transform((data, ctx) => (data === null ? null : tryParseNumber(data, ctx)));

type OrderType = z.infer<typeof initBookingModeSchema>;

const bookingParamMappings: Record<OrderType, BookingMode> = {
  houseConnection: BookingMode.houseConnectionOnly,
  infrastructure: BookingMode.ne4Only,
  productOnly: BookingMode.productOnly,
  /**
   * This is a WKK-specific case:
   * When booking a house connection with a product,
   * users must not be able to unselect the product,
   * because EECC relevance is tied to the product
   * and WKK uses the microfrontend in an EECC-relevant
   * context only.
   */
  houseConnectionAndProduct: BookingMode.houseConnectionAndProduct,
};

const UrlParamsSchema = z.object({
  initBookingMode: initBookingModeSchema.transform(data => bookingParamMappings[data]),
  street: z.string({ message: 'Adresse unvollständig: Straße fehlt' }),
  houseNumber: numberString({ error: 'Adresse unvollständig: Hausnummer fehlt' }),
  suffix: z.string().nullable(),
  zipCode: numberString({ error: 'Adresse unvollständig: PLZ fehlt' }),
  city: z.string({ message: 'Adresse unvollständig: Ort fehlt' }),
  addressId: nullableNumberString,
  channel: z.string({ message: 'Es wurde kein Kanaltyp übergeben' }),
  jwt: z.string({ message: 'Authentifizierungstoken ist erforderlich' }),
  sessionId: z.string().nullable(),
  householdId: nullableNumberString,
  customerId: nullableNumberString,
  email: z.string().email(),
});

export type UrlParams = z.infer<typeof UrlParamsSchema>;

interface UrlValidResult {
  isError: false;
  result: UrlParams;
}

interface UrlInvalidResult {
  isError: true;
  error: string;
}

export type UrlValidationResult = UrlValidResult | UrlInvalidResult;

const url = new URL(document.location.href);

const params = {
  initBookingMode: url.searchParams.get('booking-mode'),
  street: url.searchParams.get('street'),
  houseNumber: url.searchParams.get('house-number'),
  suffix: url.searchParams.get('house-number-suffix'),
  zipCode: url.searchParams.get('zip-code'),
  city: url.searchParams.get('city'),
  addressId: url.searchParams.get('address-id'),
  channel: url.searchParams.get('channel'),
  jwt: url.searchParams.get('jwt') ?? undefined,
  sessionId: url.searchParams.get('session-id'),
  householdId: url.searchParams.get('household-id'),
  customerId: url.searchParams.get('customer-id'),
  email: url.searchParams.get('email'),
};

export async function validateUrlParams(isLocal = false): Promise<UrlValidationResult> {
  try {
    if (isLocal) {
      const optionalJwtSchema = UrlParamsSchema.extend({
        jwt: z.string().optional().default(''),
      });
      const result = optionalJwtSchema.parse(params);
      return {
        isError: false,
        result,
      };
    }

    const result = UrlParamsSchema.parse(params);
    return {
      isError: false,
      result,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        isError: true,
        error: error.issues.map(e => e.message).join('\n'),
      };
    }

    return {
      isError: true,
      error:
        'Die übergebenen Parameter sind unvollständig oder fehlerhaft. Bitte Eingaben überprüfen und erneut validieren.',
    };
  }
}
